import classnames from 'classnames';
import * as PopperJS from 'popper.js';
import React, { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import './interval_field.scss';

import { Stack } from '@nerdgeschoss/shimmer-component-stack';
import { TextField } from '../../components/Form/TextField/TextField';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { t } from '../../locales/translate';
import { FormField } from '../../model/form';
import { useMediaQuery } from '../../util/useMediaQuery';
import { formatRangeOfDays } from '../../util/util';
import { Button } from '../Button/Button';
import { StyledDayPicker } from '../Form/styled_day_picker/styled_day_picker';
import { Icon } from '../Icon/Icon';
import { Text } from '../text/text';
import { getPresetDates, isIntervalDisabled } from './helpers';

export type Preset =
  | 'today'
  | 'yesterday'
  | 'weekToDate'
  | 'lastWeek'
  | 'monthToDate'
  | 'lastMonth'
  | 'last30Days'
  | 'yearToDate'
  | 'lastYear'
  | 'totalPeriod'
  | 'custom';

export type Period = 'hour' | 'day' | 'week' | 'month';

const presets: Preset[] = [
  'today',
  'yesterday',
  'weekToDate',
  'lastWeek',
  'monthToDate',
  'lastMonth',
  'last30Days',
  'yearToDate',
  'lastYear',
  'totalPeriod',
  'custom',
];

const mobilePresets: Preset[] = ['today', 'yesterday', 'lastWeek', 'last30Days', 'lastYear', 'custom'];

const intervals: Period[] = ['hour', 'day', 'week', 'month'];

type Field = {
  from: Date;
  to: Date;
  period?: Period;
};

type Props = FormField<Field> & {
  initialValue: {
    from: Date;
    to: Date;
  };
  position?: PopperJS.Placement;
  disableFutureDates?: boolean;
  dashboard?: boolean;
};

export function IntervalField({
  onChange,
  value,
  initialValue,
  position = 'bottom-start',
  disableFutureDates,
  dashboard,
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [preset, setPreset] = useState<Preset>('totalPeriod');
  const [period, setPeriod] = useState<Period>(value?.period || 'day');

  const [isDesktop] = useMediaQuery([900]);

  const [selectedDate, setSelectedDate] = useState<{ from: Date; to: Date }>({
    from: value?.from || initialValue.from,
    to: value?.to || initialValue.to,
  });

  const [displayedMonth, setDisplayedMonth] = useState<Date>(initialValue.from);

  useEffect(() => {
    if (selectedDate) {
      const isDisabled = isIntervalDisabled(selectedDate.from, selectedDate.to, period);
      if (isDisabled) {
        const periodOptions = [...intervals];

        const idxOfDisabledInterval = periodOptions.findIndex((p) => p === period);

        const intervalToSet = periodOptions[idxOfDisabledInterval + 1] || 'month';

        setPeriod(intervalToSet);
      }
    }
  }, [selectedDate]);

  useEffect(() => {
    if (value?.to && value?.from) {
      setSelectedDate(selectedDate);
    }
  }, [value]);

  const handlePresetChange = (preset: Preset): void => {
    setPreset(preset);

    if (preset === 'custom') {
      return;
    }

    if (preset === 'today' || preset === 'yesterday') {
      setPeriod('hour');
    } else {
      setPeriod('day');
    }

    if (preset === 'totalPeriod') {
      setSelectedDate(initialValue);
      setDisplayedMonth(initialValue.from);
      return;
    }

    const { startDate, endDate } = getPresetDates(preset);

    setDisplayedMonth(startDate);
    setSelectedDate({ from: startDate, to: endDate });
  };

  const presetsWithHandlers = (isDesktop ? presets : mobilePresets).map((preset) => ({
    name: preset,
    label: t.component.intervalPicker.datePresets[preset],
    handler: () => handlePresetChange(preset),
  }));

  const intervalsWithHandlers = intervals.map((interval) => {
    const isDisabled = selectedDate ? isIntervalDisabled(selectedDate.from, selectedDate.to, interval) : true;

    return {
      name: interval,
      label: t.component.intervalPicker.interval[interval],
      handler: () => {
        setPeriod(interval);
      },
      isDisabled,
    };
  });

  function getPlaceholder(): string {
    if (value) {
      return formatRangeOfDays([value.from, value.to]);
    }
    return presets.find((p) => p === preset) || '';
  }

  return (
    <div className="interval-field__container">
      <Tooltip
        onChange={setIsOpen}
        mode="click"
        open={isOpen}
        position={isDesktop ? position : 'top'}
        reference={
          <TextField
            className="interval-field"
            placeholder={getPlaceholder()}
            readOnly
            icon="calendar"
            actions={<Icon name="arrow-triangle-down--sm" color="var(--KS-Gray-2, #909090)" />}
            styleType={dashboard ? 'dashboard' : undefined}
          />
        }
        rounded={false}
        arrow={false}
        padding="none"
        className="interval-field__tooltip"
        fullReferenceWidth={!isDesktop}
      >
        <div className="interval-field__tooltip-content">
          <div className="interval-field__panel">
            {presetsWithHandlers.map((p) => (
              <button
                key={p.name}
                onClick={p.handler}
                className={classnames('interval-field__preset', {
                  'interval-field__preset--active': preset === p.name,
                })}
              >
                <Text type={preset === p.name ? 'datepicker-preset-active' : 'datepicker-preset'}>{p.label}</Text>
              </button>
            ))}
          </div>
          <div className="interval-field__picker-container">
            <Stack gap={12} gapDesktop={24}>
              <Text type="datepicker-label">{t.component.intervalPicker.selectPeriod}</Text>
              <StyledDayPicker
                numberOfMonths={isDesktop ? 2 : 1}
                required
                onSelect={(date: DateRange) => {
                  if (date.from && date.to) {
                    setSelectedDate({ from: date.from, to: date.to });
                    setPreset('custom');
                  }
                }}
                selected={selectedDate}
                mode="range"
                disabled={
                  disableFutureDates
                    ? {
                        after: new Date(),
                      }
                    : undefined
                }
                defaultMonth={displayedMonth}
                month={displayedMonth}
                onMonthChange={(month) => setDisplayedMonth(month)}
              />
              <Stack gap={20} lineDesktop align="end" alignDesktop="center">
                <div className="interval-field__intervals">
                  <Stack gap={0} line justify="space-between" align="center">
                    <Text type="datepicker-label">{t.component.intervalPicker.intervalText}</Text>
                    <Stack gap={16} line>
                      {intervalsWithHandlers.map((i) => (
                        <button
                          disabled={i.isDisabled}
                          key={i.name}
                          onClick={i.handler}
                          className={classnames('interval-field__interval-button', {
                            'interval-field__interval-button--selected': period === i.name,
                            'interval-field__interval-button--disabled': i.isDisabled,
                          })}
                        >
                          <Text type={period === i.name ? 'datepicker-interval-active' : 'datepicker-interval'}>
                            {i.label}
                          </Text>
                        </button>
                      ))}
                    </Stack>
                  </Stack>
                </div>
                <Button
                  type="primary"
                  title={t.component.intervalPicker.apply}
                  onClick={() => {
                    onChange?.({ ...selectedDate, period });
                    setIsOpen(false);
                  }}
                  disabled={selectedDate.from > selectedDate.to}
                />
              </Stack>
            </Stack>
          </div>
        </div>
      </Tooltip>
    </div>
  );
}
